import React, { CSSProperties, ReactElement, ReactNode } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled, { ThemeProvider } from 'styled-components'

import { theme } from '../theme'

export const SInputLabel = styled.label`
    font-size: 12px;
    color: #535353;
    font-weight: 200;
    position: absolute;
    pointer-events: none;
    top: -16px;
    left: 10px;
    transition: all 0.2s ease;
`

export const SInput = styled.input<{ password?: boolean; hasText?: boolean; inverted?: boolean }>`
    display: block;
    width: 100%;
    padding: 10px;
    padding-right: ${({ password }) => (password ? '55px' : '10px')};
    margin-bottom: 20px;
    border-radius: 0;
    line-height: 1.2em;
    height: 40px;
    background-color: transparent;
    background: none;
    border: none;
    outline: none;

    &[type='password'] {
        ${({ hasText }) => {
            if (hasText)
                return `
                    font-family: Verdana, sans-serif;
                    letter-spacing: 0.1em;
                `
        }}
    }

    &.hidden {
        opacity: 0;
        position: relative;
        left: -100%;
    }

    ::placeholder {
        color: ${({ theme, inverted }) => (inverted ? theme.font.lightestColor : theme.font.midDarkColor)};
        opacity: 1;
        font-weight: 300;
    }
`

export const SInputGroup = styled.div<{ error: boolean; inverted: boolean }>`
    position: relative;

    ${SInputLabel} {
        color: ${({ error, inverted }) => {
            if (error && inverted) return '#FF8B7C'
            if (error) return '#DF0017'
            if (inverted) return '#fff'
            return '#000'
        }};
    }

    ${SInput} {
        border: ${({ inverted, error }) => {
            if (error && inverted) return '1px solid #FF8B7C'
            if (error) return '1px solid #df0017'
            return 'none'
        }};
        border-bottom: 1px solid
            ${({ inverted, error }) => {
                if (error && inverted) return '#FF8B7C'
                if (error) return '#df0017'
                if (inverted) return '#fff'
                return '#535353'
            }};
    }

    //Removes the ugly autofill background
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
        transition: background-color 5000s;
        -webkit-text-fill-color: ${({ inverted }) => (inverted ? '#fff' : '#000')} !important;
    }
`

export const SErrorMsg = styled.div<{ inverted: boolean }>`
    display: block;
    height: 15px;
    position: relative;
    top: 10px;
    margin-top: -30px;
    padding: 2px 10px;
    font-size: 14px;
    color: ${({ inverted }) => (inverted ? '#FF8B7C' : '#df0017')};
`

const SMessage = styled(SErrorMsg)`
    color: ${({ theme }) => theme.font.midDarkColor};
`

type InputGroupProps = {
    children: ReactNode | Array<ReactNode>
    inputGroupRef?: React.Ref<HTMLDivElement>
    label?: string | null
    errorTestId?: string
    errorMsg?: string | React.ReactElement | null
    message?: string | React.ReactElement | null
    inverted?: boolean
    className?: ClassName
    style?: CSSProperties
}

export function InputGroup({
    children,
    label,
    errorTestId,
    errorMsg,
    message,
    inverted,
    className,
    style,
    inputGroupRef,
}: InputGroupProps): ReactElement {
    return (
        <div className={cn(className)} style={style} ref={inputGroupRef}>
            <SInputGroup error={errorMsg ? true : false} inverted={inverted || false}>
                {label && <SInputLabel>{label}</SInputLabel>}
                {children}
            </SInputGroup>

            {message && (
                <ThemeProvider theme={theme}>
                    <SMessage inverted={inverted || false}>{message}</SMessage>
                </ThemeProvider>
            )}

            {errorMsg && (
                <SErrorMsg data-testid={errorTestId} inverted={inverted || false}>
                    {errorMsg}
                </SErrorMsg>
            )}
        </div>
    )
}
